import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";
import "./AdminUsers.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* Permission summary columns — derived from each role's permission list */
function derivePerms(permissions) {
  if (!permissions) return {};
  const p = new Set(permissions);
  if (p.has("*")) return { tools: true, studio: true, certificar: true, adminNews: true, adminUsers: true, seeAll: true };
  return {
    tools: [...p].some(x => x.startsWith("tools.")),
    studio: [...p].some(x => x.startsWith("studio.")),
    certificar: p.has("certificar"),
    adminNews: p.has("admin.news"),
    adminUsers: p.has("admin.users"),
    seeAll: p.has("see.all.images"),
  };
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ id: "", name: "", email: "", password: "", role: "viewer" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetch(`${API_URL}/admin/users`, { headers }),
        fetch(`${API_URL}/admin/roles`, { headers }),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (rolesRes.ok) setRoles(await rolesRes.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* Build role → permissions lookup from API data */
  const rolePermsMap = {};
  roles.forEach(r => { rolePermsMap[r.id] = r.permissions || []; });

  const updateRole = async (userId, role) => {
    await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/role`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ role }),
    });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
  };

  const toggleActive = async (userId, active) => {
    await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/active`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ active: active ? 1 : 0 }),
    });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, active: active ? 1 : 0 } : u));
  };

  const createUser = async () => {
    if (!newUser.id || !newUser.name || !newUser.password) {
      setMsg("ID, nombre y contrasena son obligatorios");
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const res = await fetch(`${API_URL}/admin/users`, {
        method: "POST",
        headers,
        body: JSON.stringify(newUser),
      });
      if (res.ok) {
        setShowNew(false);
        setNewUser({ id: "", name: "", email: "", password: "", role: "viewer" });
        loadData();
      } else {
        const data = await res.json();
        setMsg(data.detail || "Error al crear usuario");
      }
    } finally {
      setSaving(false);
    }
  };

  const perms = (role) => derivePerms(rolePermsMap[role] || []);
  const roleIds = roles.map(r => r.id);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>Gestion de Usuarios</h2>
        <button className="admin-btn-new" onClick={() => setShowNew(!showNew)}>
          {showNew ? "Cancelar" : "+ Nuevo usuario"}
        </button>
      </div>

      {showNew && (
        <div className="admin-new-form">
          <input placeholder="ID (ej: nombre.apellido)" value={newUser.id} onChange={e => setNewUser({ ...newUser, id: e.target.value })} />
          <input placeholder="Nombre completo" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
          <input placeholder="Email" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
          <input placeholder="Contrasena" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
          <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
            {roleIds.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="admin-btn-save" onClick={createUser} disabled={saving}>
            {saving ? "Creando..." : "Crear"}
          </button>
          {msg && <span className="admin-msg">{msg}</span>}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">Cargando...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Tools</th>
              <th>Studio</th>
              <th>Certificar</th>
              <th>News</th>
              <th>Usuarios</th>
              <th>Ver todo</th>
              <th>Activo</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const p = perms(u.role);
              return (
                <tr key={u.id} className={!u.active ? "admin-row-inactive" : ""}>
                  <td className="admin-cell-id">{u.id}</td>
                  <td>{u.name}</td>
                  <td className="admin-cell-email">{u.email || "\u2014"}</td>
                  <td>
                    <select value={u.role} onChange={e => updateRole(u.id, e.target.value)} className="admin-select-role">
                      {roleIds.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="admin-cell-check">{p.tools ? "\u2713" : "\u2717"}</td>
                  <td className="admin-cell-check">{p.studio ? "\u2713" : "\u2717"}</td>
                  <td className="admin-cell-check">{p.certificar ? "\u2713" : "\u2717"}</td>
                  <td className="admin-cell-check">{p.adminNews ? "\u2713" : "\u2717"}</td>
                  <td className="admin-cell-check">{p.adminUsers ? "\u2713" : "\u2717"}</td>
                  <td className="admin-cell-check">{p.seeAll ? "\u2713" : "\u2717"}</td>
                  <td>
                    <input type="checkbox" checked={!!u.active} onChange={e => toggleActive(u.id, e.target.checked)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
